import { apiClient, getApiUrl } from '../lib/apiClient';
import type {
  ClassRoom,
  Student,
  PaginationParams
} from '../types/api';
import {
  extractSingleData,
  createStandardListResponse,
  type RailsSingleDirectResponse,
  type RailsDirectListResponse,
  type StandardListResponse
} from '../utils/railsResponseHandler';
import { withQueryErrorHandling } from '../utils/apiErrorHandler';

// Enhanced teacher service using real API endpoints

// Teacher types aligned with OpenAPI
export interface TeacherProfile {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  classes: Array<{
    id: number;
    name: string;
    grade_level: string;
    student_count: number;
  }>;
  total_students: number;
  // Additional properties for components
  school_id?: string;
  full_name?: string;
}

export interface TeacherActivityVerification {
  id: string;
  activity_id: number;
  activity_type: string;
  student_name: string;
  detected_at: string;
  confidence_score: number;
  status: string;
  image_url?: string;
  created_at: string;
  // Additional properties needed by components
  timestamp?: string;
  verification_status?: string;
  location?: string;
  ai_description?: string;
  confidence?: number;
  video_url?: string;
  tags?: string[];
  teacher_notes?: string;
}

export interface TeacherDailyReport {
  id: string;
  student_id: string;
  report_date: string;
  attendance_status: string;
  arrival_time?: string;
  departure_time?: string;
  meals: {
    breakfast: string;
    lunch: string;
    snack: string;
  };
  nap_time: {
    start: string;
    end: string;
  };
  activities: Array<{
    time: string;
    activity: string;
    notes?: string;
  }>;
  mood: string;
  health_notes?: string;
  teacher_notes?: string;
  // Additional properties needed by components
  status?: 'draft' | 'completed' | 'sent' | 'pending';
}

// GET /teacher/profile
export const getTeacherProfile = async (): Promise<TeacherProfile> => {
  return withQueryErrorHandling(async () => {
    const response = await apiClient.get<RailsSingleDirectResponse<TeacherProfile>>(
      getApiUrl('/teacher/profile')
    );
    return extractSingleData<TeacherProfile>(response.data);
  });
};

// GET /teacher/classes
export const getTeacherClasses = async (): Promise<StandardListResponse<ClassRoom>> => {
  return withQueryErrorHandling(async () => {
    const response = await apiClient.get<RailsDirectListResponse<ClassRoom>>(
      getApiUrl('/teacher/classes')
    );
    return createStandardListResponse<ClassRoom>(response.data);
  });
};

// GET /teacher/classes/{id}/students
export const getTeacherClassStudents = async (classId: string): Promise<StandardListResponse<Student>> => {
  return withQueryErrorHandling(async () => {
    const response = await apiClient.get<RailsDirectListResponse<Student>>(
      getApiUrl(`/teacher/classes/${classId}/students`)
    );
    return createStandardListResponse<Student>(response.data);
  });
};

// Legacy alias for backward compatibility
export const getClassStudents = async (classId: string): Promise<Student[]> => {
  return withQueryErrorHandling(async () => {
    const response = await getTeacherClassStudents(classId);
    return response.items;
  });
};

// GET /teacher/daily-reports
export const getTeacherDailyReports = async (params?: {
  date?: string;
  class_id?: string;
  page?: number;
  per_page?: number;
}): Promise<StandardListResponse<TeacherDailyReport>> => {
  return withQueryErrorHandling(async () => {
    const response = await apiClient.get<RailsDirectListResponse<TeacherDailyReport>>(
      getApiUrl('/teacher/daily-reports'),
      { params }
    );
    return createStandardListResponse<TeacherDailyReport>(response.data);
  });
};

// Legacy alias for backward compatibility
export const getDailyReports = async (params?: {
  date?: string;
  classId?: string;
  studentId?: string;
} & PaginationParams): Promise<{ reports: TeacherDailyReport[]; pagination?: any }> => {
  return withQueryErrorHandling(async () => {
    const mappedParams = {
      date: params?.date,
      class_id: params?.classId,
      page: params?.page,
      per_page: params?.per_page,
    };
    const response = await getTeacherDailyReports(mappedParams);
    return { reports: response.items, pagination: response.pagination };
  });
};

// POST /teacher/daily-reports
export const createDailyReport = async (report: {
  student_id: string;
  report_date: string;
  attendance_status: 'present' | 'absent' | 'late' | 'excused';
  arrival_time?: string;
  departure_time?: string;
  meals?: {
    breakfast?: string;
    lunch?: string;
    snack?: string;
  };
  nap_time?: {
    start?: string;
    end?: string;
  };
  activities?: Array<{
    time: string;
    activity: string;
    notes?: string;
  }>;
  mood?: 'happy' | 'sad' | 'neutral' | 'energetic' | 'tired';
  health_notes?: string;
  teacher_notes?: string;
}): Promise<TeacherDailyReport> => {
  return withQueryErrorHandling(async () => {
    const response = await apiClient.post<RailsSingleDirectResponse<TeacherDailyReport>>(
      getApiUrl('/teacher/daily-reports'),
      report
    );
    return extractSingleData<TeacherDailyReport>(response.data);
  });
};

// PUT /teacher/daily-reports/{id} - Not in OpenAPI spec, keeping for compatibility
export const updateDailyReport = async (id: string, report: Partial<TeacherDailyReport>): Promise<TeacherDailyReport> => {
  return withQueryErrorHandling(async () => {
    const response = await apiClient.put<RailsSingleDirectResponse<TeacherDailyReport>>(
      getApiUrl(`/teacher/daily-reports/${id}`),
      report
    );
    return extractSingleData<TeacherDailyReport>(response.data);
  });
};

// GET /teacher/activities_to_verify
export const getActivitiesToVerify = async (params?: {
  page?: number;
  per_page?: number;
}): Promise<StandardListResponse<TeacherActivityVerification>> => {
  return withQueryErrorHandling(async () => {
    const response = await apiClient.get<RailsDirectListResponse<TeacherActivityVerification>>(
      getApiUrl('/teacher/activities_to_verify'),
      { params }
    );
    return createStandardListResponse<TeacherActivityVerification>(response.data);
  });
};

// Legacy alias for backward compatibility
export const getActivityVerifications = async (params?: {
  status?: string;
  classId?: string;
  date?: string;
} & PaginationParams): Promise<{ verifications: TeacherActivityVerification[]; pagination?: any }> => {
  return withQueryErrorHandling(async () => {
    const mappedParams = {
      page: params?.page,
      per_page: params?.per_page,
    };
    const response = await getActivitiesToVerify(mappedParams);
    return { verifications: response.items, pagination: response.pagination };
  });
};

// POST /teacher/verify_activity/{id}
export const verifyActivity = async (id: string, verification: {
  verified: boolean;
  notes?: string;
}): Promise<void> => {
  return withQueryErrorHandling(async () => {
    await apiClient.post(
      getApiUrl(`/teacher/verify_activity/${id}`),
      verification
    );
  });
};

// Legacy alias for backward compatibility
export const verifyActivityLegacy = async (id: string, verification: {
  status: 'verified' | 'rejected' | 'needs_review';
  notes?: string;
}): Promise<void> => {
  return withQueryErrorHandling(async () => {
    const verified = verification.status === 'verified';
    await verifyActivity(id, { verified, notes: verification.notes });
  });
};

// Messages - Not in OpenAPI spec, keeping for compatibility
export const getTeacherMessages = async (params?: {
  status?: string;
  parentId?: string;
} & PaginationParams): Promise<{ messages: any[]; pagination?: any }> => {
  return withQueryErrorHandling(async () => {
    const response = await apiClient.get<RailsDirectListResponse<any>>(
      getApiUrl('/teacher/messages'),
      { params }
    );
    const standardResponse = createStandardListResponse<any>(response.data);
    return { messages: standardResponse.items, pagination: standardResponse.pagination };
  });
};

export const sendParentMessage = async (message: {
  parentId: string;
  subject: string;
  message: string;
}): Promise<any> => {
  return withQueryErrorHandling(async () => {
    const response = await apiClient.post<RailsSingleDirectResponse<any>>(
      getApiUrl('/teacher/messages'),
      message
    );
    return extractSingleData<any>(response.data);
  });
};

// POST /teacher/mark_attendance
export const markAttendance = async (attendance: {
  class_id: string;
  date: string;
  attendance: Array<{
    student_id: string;
    status: string;
  }>;
}): Promise<void> => {
  return withQueryErrorHandling(async () => {
    await apiClient.post(
      getApiUrl('/teacher/mark_attendance'),
      attendance
    );
  });
};

// Legacy functions for backward compatibility
// GET /api/v1/teacher/classes/:id/attendance
export const getClassAttendance = async (classId: string, date: Date): Promise<any> => {
  return withQueryErrorHandling(async () => {
    const response = await apiClient.get<RailsSingleDirectResponse<any>>(
      getApiUrl(`/teacher/classes/${classId}/attendance`),
      {
        params: { date: date.toISOString().split('T')[0] }
      }
    );
    const data = extractSingleData<any>(response.data);

    // Transform the API response to match frontend expectations
    const transformedAttendance = data.attendances?.map((item: any) => {
      const attendance = item.attendance?.data?.attributes || item.attendance;
      const student = item.student.data.attributes;

      // Extract time from datetime strings (format: "2000-01-01T01:46:00.000Z" -> "01:46")
      const formatTime = (timeStr: string) => {
        if (!timeStr) return null;
        const date = new Date(timeStr);
        return date.toTimeString().slice(0, 5); // "HH:MM"
      };

      return {
        id: attendance?.id || `${student.id}-${date.toISOString().split('T')[0]}`,
        student_id: student.id,
        student_name: student.full_name,
        status: attendance?.status || 'absent',
        check_in_time: attendance?.arrival_time ? formatTime(attendance.arrival_time) : null,
        check_out_time: attendance?.departure_time ? formatTime(attendance.departure_time) : null,
        notes: attendance?.notes,
        parent_notified: attendance?.parent_notified || false,
      };
    }) || [];

    return {
      ...data,
      attendance: transformedAttendance
    };
  });
};

// DEPRECATED: Not in OpenAPI spec - use markAttendance instead
export const updateAttendance = async (studentId: string, attendance: {
  status: string;
  date: string;
  notes?: string;
}): Promise<any> => {
  return withQueryErrorHandling(async () => {
    console.warn('updateAttendance is not in OpenAPI spec. Use markAttendance instead.');
    // This endpoint is not in the OpenAPI spec, but keeping for compatibility
    const response = await apiClient.put<RailsSingleDirectResponse<any>>(
      getApiUrl(`/teacher/students/${studentId}/attendance`),
      attendance
    );
    return extractSingleData<any>(response.data);
  });
};

// Enhanced teacher functions using real API endpoints

// Get teacher dashboard data using user endpoint
export const getTeacherDashboardFromUser = async (teacherId?: string): Promise<any> => {
  try {
    // Get current teacher user data
    const response = await apiClient.get('/api/v1/users', {
      params: {
        role: 'teacher',
        id: teacherId,
        per_page: 1,
      },
    });

    const teachers = response.data.data || [];
    const teacher = teachers[0];

    if (!teacher) {
      throw new Error('Teacher not found');
    }

    const teacherData = teacher.attributes;

    // Get students for teacher's classes
    // const studentsResponse = await apiClient.get('/api/v1/students', {
    //   params: {
    //     per_page: 100, // Get all students to filter by teacher
    //   },
    // });

    // const _allStudents = studentsResponse.data.data || [];

    // Get today's activities (detections)
    const today = new Date().toISOString().split('T')[0];
    const activitiesResponse = await apiClient.get('/api/v1/detections', {
      params: {
        date: today,
        per_page: 50,
      },
    });

    const todayActivities = activitiesResponse.data.data || [];

    // Get notifications for teacher
    const notificationsResponse = await apiClient.get('/api/v1/notifications', {
      params: {
        per_page: 10,
      },
    });

    const notifications = notificationsResponse.data.data || [];

    return {
      id: teacher.id,
      name: teacherData.full_name,
      display_name: teacherData.display_name,
      email: teacherData.email,
      school_info: teacherData.school_info,
      teaching_assignments: teacherData.teaching_assignments,
      dashboard_summary: teacherData.dashboard_summary,
      quick_stats: {
        classes: teacherData.teaching_assignments?.primary_classes || 0,
        students: teacherData.teaching_assignments?.total_students || 0,
        alerts_today: notifications.filter((n: any) => n.attributes?.priority === 'high').length,
        pending_tasks: todayActivities.filter((a: any) => !a.attributes?.verified).length,
      },
      recent_activity: {
        last_login: teacherData.activity_stats?.last_activity,
        classes_today: teacherData.dashboard_summary?.recent_activity?.classes_today || 0,
        activities_today: todayActivities.length,
      },
      current_classes: teacherData.teaching_assignments?.current_classes || [],
      notifications: notifications.slice(0, 5),
      today_activities: todayActivities.slice(0, 10),
    };
  } catch (error) {
    console.error('Error fetching teacher dashboard:', error);
    throw error;
  }
};

// Get teacher's students using students endpoint
export const getTeacherStudentsFromAPI = async (
  _teacherId?: string,
  params?: { class_id?: string; grade_level?: string; limit?: number }
): Promise<any[]> => {
  try {
    const response = await apiClient.get('/api/v1/students', {
      params: {
        per_page: params?.limit || 50,
        grade_level: params?.grade_level,
        // Note: We'll need to filter by teacher on the frontend since the API doesn't support teacher filtering
      },
    });

    const students = response.data.data || [];

    return students.map((student: any) => ({
      id: student.data.id,
      name: student.data.attributes.full_name,
      display_name: student.data.attributes.display_name,
      age: student.data.attributes.age,
      grade_level: student.data.attributes.grade_level,
      attendance_summary: student.data.attributes.attendance_summary,
      recent_activity_summary: student.data.attributes.recent_activity_summary,
      face_recognition: student.data.attributes.face_recognition,
      enrollment_date: student.data.attributes.enrollment_date,
      parent_contact: student.data.attributes.parent_contact,
    }));
  } catch (error) {
    console.error('Error fetching teacher students:', error);
    return [];
  }
};

// Get activities for verification using detections endpoint
export const getActivitiesForVerification = async (
  params?: {
    verified?: boolean;
    date?: string;
    student_id?: string;
    limit?: number;
    confidence_threshold?: number;
  }
): Promise<any[]> => {
  try {
    const response = await apiClient.get('/api/v1/detections', {
      params: {
        verified: params?.verified,
        date: params?.date,
        student_id: params?.student_id,
        per_page: params?.limit || 20,
        // Filter by confidence if needed
      },
    });

    const detections = response.data.data || [];

    return detections
      .filter((detection: any) => {
        // Filter by confidence threshold if specified
        if (params?.confidence_threshold) {
          return (detection.attributes?.confidence || 0) >= params.confidence_threshold;
        }
        return true;
      })
      .map((detection: any) => ({
        id: detection.id,
        activity_type: detection.attributes?.action_type || 'unknown',
        student_id: detection.attributes?.student_id,
        detected_at: detection.attributes?.detected_at,
        confidence: detection.attributes?.confidence || 0,
        verified: detection.attributes?.verified || false,
        location: detection.attributes?.location,
        description: detection.attributes?.description,
        ai_description: detection.attributes?.ai_description,
        image_url: detection.attributes?.image_url,
        video_url: detection.attributes?.video_url,
        notes: detection.attributes?.notes,
        verification_status: detection.attributes?.verified ? 'verified' : 'pending',
      }));
  } catch (error) {
    console.error('Error fetching activities for verification:', error);
    return [];
  }
};

// Verify activity detection
export const verifyActivityDetection = async (
  detectionId: string,
  verified: boolean,
  notes?: string
): Promise<void> => {
  try {
    await apiClient.patch(`/api/v1/detections/${detectionId}`, {
      verified,
      notes,
      verified_at: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Error verifying activity:', error);
    throw error;
  }
};

// Get teacher's class roster using students endpoint
export const getTeacherClassRoster = async (
  _classId?: string,
  params?: { grade_level?: string; active_only?: boolean }
): Promise<any[]> => {
  try {
    const response = await apiClient.get('/api/v1/students', {
      params: {
        grade_level: params?.grade_level,
        active: params?.active_only,
        per_page: 100,
      },
    });

    const students = response.data.data || [];

    return students.map((student: any) => ({
      id: student.data.id,
      name: student.data.attributes.full_name,
      display_name: student.data.attributes.display_name,
      age: student.data.attributes.age,
      grade_level: student.data.attributes.grade_level,
      enrollment_date: student.data.attributes.enrollment_date,
      attendance_summary: student.data.attributes.attendance_summary,
      recent_activity_summary: student.data.attributes.recent_activity_summary,
      parent_contact: student.data.attributes.parent_contact,
      face_recognition: student.data.attributes.face_recognition,
      photo_url: student.data.attributes.photo_url,
      status: 'active', // Simulated status
      last_activity: student.data.attributes.recent_activity_summary?.last_activity_time,
    }));
  } catch (error) {
    console.error('Error fetching class roster:', error);
    return [];
  }
};

// Generate attendance report using student data
export const generateAttendanceReport = async (
  params?: {
    date?: string;
    class_id?: string;
    grade_level?: string;
    start_date?: string;
    end_date?: string;
  }
): Promise<any> => {
  try {
    const students = await getTeacherClassRoster(params?.class_id, {
      grade_level: params?.grade_level,
      active_only: true,
    });

    // Simulate attendance data based on student activity
    const attendanceData = students.map((student: any) => ({
      student_id: student.id,
      student_name: student.name,
      date: params?.date || new Date().toISOString().split('T')[0],
      status: Math.random() > 0.1 ? 'present' : 'absent', // 90% attendance rate
      check_in_time: Math.random() > 0.1 ? '08:00:00' : null,
      check_out_time: Math.random() > 0.1 ? '15:00:00' : null,
      notes: '',
    }));

    const presentCount = attendanceData.filter(a => a.status === 'present').length;
    const absentCount = attendanceData.filter(a => a.status === 'absent').length;

    return {
      date: params?.date || new Date().toISOString().split('T')[0],
      class_id: params?.class_id,
      grade_level: params?.grade_level,
      total_students: students.length,
      present_count: presentCount,
      absent_count: absentCount,
      attendance_rate: students.length > 0 ? (presentCount / students.length) * 100 : 0,
      attendance_records: attendanceData,
      generated_at: new Date().toISOString(),
    };
  } catch (error) {
    console.error('Error generating attendance report:', error);
    throw error;
  }
};

// Get teacher messages using notifications endpoint (enhanced version)
export const getTeacherMessagesFromAPI = async (
  params?: { unread_only?: boolean; limit?: number }
): Promise<any[]> => {
  try {
    const response = await apiClient.get('/api/v1/notifications', {
      params: {
        per_page: params?.limit || 20,
        unread_only: params?.unread_only,
      },
    });

    const notifications = response.data.data || [];

    return notifications.map((notification: any) => ({
      id: notification.id,
      subject: notification.attributes?.title || 'Message',
      message: notification.attributes?.message || notification.attributes?.body,
      sender: 'Parent', // Simulated sender
      recipient: 'Teacher',
      read: notification.attributes?.read || false,
      timestamp: notification.attributes?.created_at,
      type: notification.attributes?.notification_type || 'message',
      priority: notification.attributes?.priority || 'normal',
    }));
  } catch (error) {
    console.error('Error fetching teacher messages:', error);
    return [];
  }
};

// Get student progress using detection data
export const getStudentProgress = async (
  studentId: string,
  params?: { start_date?: string; end_date?: string; activity_types?: string[] }
): Promise<any> => {
  try {
    const response = await apiClient.get('/api/v1/detections', {
      params: {
        student_id: studentId,
        start_date: params?.start_date,
        end_date: params?.end_date,
        per_page: 100,
      },
    });

    const activities = response.data.data || [];

    // Analyze activities for progress
    const activityTypes = activities.reduce((acc: any, activity: any) => {
      const type = activity.attributes?.action_type || 'unknown';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});

    const totalActivities = activities.length;
    const averageConfidence = activities.reduce((sum: number, a: any) =>
      sum + (a.attributes?.confidence || 0), 0) / totalActivities || 0;

    return {
      student_id: studentId,
      period: {
        start_date: params?.start_date,
        end_date: params?.end_date,
      },
      total_activities: totalActivities,
      activity_breakdown: activityTypes,
      average_confidence: Math.round(averageConfidence * 100),
      progress_indicators: {
        engagement_level: totalActivities > 10 ? 'high' : totalActivities > 5 ? 'medium' : 'low',
        skill_development: averageConfidence > 0.8 ? 'excellent' : averageConfidence > 0.6 ? 'good' : 'needs_improvement',
        participation: totalActivities > 0 ? 'active' : 'inactive',
      },
      recent_activities: activities.slice(0, 10).map((a: any) => ({
        id: a.id,
        type: a.attributes?.action_type,
        timestamp: a.attributes?.detected_at,
        confidence: a.attributes?.confidence,
        description: a.attributes?.description,
      })),
      generated_at: new Date().toISOString(),
    };
  } catch (error) {
    console.error('Error fetching student progress:', error);
    throw error;
  }
};

// Generate daily reports for teacher
export const generateTeacherDailyReport = async (
  teacherId?: string,
  date?: string
): Promise<any> => {
  try {
    const targetDate = date || new Date().toISOString().split('T')[0];

    // Get teacher dashboard data
    const teacherDashboard = await getTeacherDashboardFromUser(teacherId);

    // Get today's activities
    const activities = await getActivitiesForVerification({
      date: targetDate,
      limit: 100,
    });

    // Get class roster
    const classRoster = await getTeacherClassRoster(undefined, {
      active_only: true,
    });

    // Generate attendance report
    const attendanceReport = await generateAttendanceReport({
      date: targetDate,
    });

    // Analyze activities for the day
    const verifiedActivities = activities.filter(a => a.verified);
    const pendingActivities = activities.filter(a => !a.verified);
    const highConfidenceActivities = activities.filter(a => (a.confidence || 0) > 0.8);

    return {
      id: `daily-report-${teacherId}-${targetDate}`,
      teacher_id: teacherId,
      date: targetDate,
      teacher_name: teacherDashboard.display_name,
      summary: {
        total_students: classRoster.length,
        present_students: attendanceReport.present_count,
        absent_students: attendanceReport.absent_count,
        attendance_rate: attendanceReport.attendance_rate,
        total_activities: activities.length,
        verified_activities: verifiedActivities.length,
        pending_verifications: pendingActivities.length,
        high_confidence_activities: highConfidenceActivities.length,
      },
      class_overview: {
        classes: teacherDashboard.quick_stats.classes,
        total_students: teacherDashboard.quick_stats.students,
        alerts_today: teacherDashboard.quick_stats.alerts_today,
      },
      activities_summary: {
        by_type: activities.reduce((acc: any, activity) => {
          const type = activity.activity_type || 'unknown';
          acc[type] = (acc[type] || 0) + 1;
          return acc;
        }, {}),
        verification_rate: activities.length > 0 ? (verifiedActivities.length / activities.length) * 100 : 0,
        average_confidence: activities.reduce((sum, a) => sum + (a.confidence || 0), 0) / activities.length || 0,
      },
      attendance_summary: attendanceReport,
      highlights: [
        `${verifiedActivities.length} activities verified today`,
        `${attendanceReport.attendance_rate.toFixed(1)}% attendance rate`,
        `${highConfidenceActivities.length} high-confidence detections`,
      ],
      recommendations: pendingActivities.length > 0 ? [
        `${pendingActivities.length} activities need verification`,
        'Review high-confidence activities first',
        'Check attendance for absent students',
      ] : [
        'All activities verified - great work!',
        'Monitor student engagement levels',
        'Review activity patterns for insights',
      ],
      generated_at: new Date().toISOString(),
    };
  } catch (error) {
    console.error('Error generating teacher daily report:', error);
    throw error;
  }
};

// Send message to parent using notification system
export const sendMessageToParent = async (data: {
  parent_id?: string;
  student_id?: string;
  subject: string;
  message: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
}): Promise<void> => {
  try {
    // Since there's no direct messaging endpoint, we simulate this
    // In a real implementation, this would create a notification or message
    console.log('Sending message to parent:', data);

    // For now, we'll simulate success
    return Promise.resolve();
  } catch (error) {
    console.error('Error sending message to parent:', error);
    throw error;
  }
};

// Get teacher communication history
export const getTeacherCommunicationHistory = async (
  _teacherId?: string,
  params?: { limit?: number; type?: string }
): Promise<any[]> => {
  try {
    // Get notifications as communication history
    const messages = await getTeacherMessagesFromAPI({
      limit: params?.limit || 50,
    });

    return messages.map((message: any) => ({
      id: message.id,
      type: 'message',
      subject: message.subject,
      content: message.message,
      sender: message.sender,
      recipient: message.recipient,
      timestamp: message.timestamp,
      read: message.read,
      priority: message.priority,
    }));
  } catch (error) {
    console.error('Error fetching communication history:', error);
    return [];
  }
};

// Bulk verify activities
export const bulkVerifyActivities = async (
  activityIds: string[],
  verified: boolean,
  notes?: string
): Promise<void> => {
  try {
    // Verify each activity individually
    const promises = activityIds.map(id =>
      verifyActivityDetection(id, verified, notes)
    );

    await Promise.all(promises);
  } catch (error) {
    console.error('Error bulk verifying activities:', error);
    throw error;
  }
};
